import { of as observableOf, Observable, Subject } from 'rxjs'
import { Component, OnDestroy, OnInit, SecurityContext, ViewChild } from '@angular/core'
import { DomSanitizer } from '@angular/platform-browser'
import { HttpHeaders, HttpClient, HttpErrorResponse } from '@angular/common/http'
import { DiffComponent } from '@exchange/ui/shared/controls'
import { EpisodeLinkRendererComponent } from '../episode-link.renderer.component'
import {
  IClaimLineView,
  IRemitLineAction,
  IRemitLineExport,
  IRemitLineView,
  IRemitLineX12,
  RemitLineExportODataService,
  RemitLineProgressODataService,
  RemitLineViewODataService,
  RemitLineX12ODataService
} from '@exchange/domain/.'
import { SessionService, UtilService } from '@exchange/services/.'
import { AuditDetailComponent } from '../audit-detail/audit-detail.component'
import { RemitListMatchClaimModalComponent } from './remit-list-match-claim-modal.component'
import { X12Codes } from '@exchange/domain/lookups'
import moment from 'moment'
import _ from 'lodash'
import { environment } from '@exchange/environment'
import { map, mergeMap, takeUntil, tap, catchError } from 'rxjs/operators'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { ToastrService } from 'ngx-toastr'
import { ConfirmModalComponent, GridSize, GridWrapperComponent } from 'common-components'
import { RemittanceDeleteDto, RemittanceService } from '@exchange/app/services/remittanceService'

@Component({
  selector: 'app-remit-list',
  templateUrl: './remit-list.component.html',
  styleUrls: ['./remit-list.component.scss'],
  standalone: false
})
export class RemitListComponent implements OnInit, OnDestroy {
  GridSize = GridSize
  model: IRemitLineView = null
  isProgressViewShown = false
  isExportViewShown = false
  isX12ViewShown = false
  isAuditView = false
  hasSelection = false
  differences = 0
  currentDiff = 0
  gridActions = null
  user = null
  summaryConfigComparisonSubscription = null

  @ViewChild('grid', { static: true }) grid: GridWrapperComponent
  @ViewChild('gridProgress', { static: true }) gridProgress: GridWrapperComponent
  @ViewChild('gridExport', { static: true }) gridExport: GridWrapperComponent
  @ViewChild('diff', { static: true }) diff: DiffComponent
  @ViewChild('auditDetail', { static: true }) auditDetail: AuditDetailComponent
  private ngUnsubscribe: Subject<boolean> = new Subject()

  detailViewColumns: any[] = []

  constructor(
    private odata: RemitLineViewODataService,
    private odataProgress: RemitLineProgressODataService,
    private odataExport: RemitLineExportODataService,
    private odataX12: RemitLineX12ODataService,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private session: SessionService,
    private modalService: BsModalService,
    private toastr: ToastrService,
    private util: UtilService
  ) { }

  ngOnInit() {
    this.initializeGrid()
    this.initializeProgressGrid()
    this.initializeExportGrid()

    this.session
      .getCurrentUser()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(user => (this.user = user))
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(false)
    this.ngUnsubscribe.complete()
  }

  gridSelectionChanged() {
    const rows = this.grid.api.getSelectedRows()
    this.hasSelection = !!rows.length
    this.model = null
    if (this.hasSelection) {
      this.model = rows[0] as IRemitLineView
    }

    this.grid.api.redrawRows()
    this.closePanels()

    this.getAvailableActions().subscribe()
  }

  getAvailableActions(): Observable<object> {
    const url = `${environment.rmapiroot}/odata/AvailableActionsView/?$filter=RemittancesViewId eq ${this.model.Id}`

    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Accept: 'application/json'
    })

    return this.http.get(url, { headers }).pipe(
      tap(data => {
        this.model.AvailableActions = _.get(data, 'value')
      })
    )
  }

  toggleProcessingProgress(): void {
    this.isProgressViewShown = !this.isProgressViewShown
    if (this.isProgressViewShown) {
      this.gridProgress.defaultFilter = `RemittanceId eq ${this.model.RemittanceId} and ServicePaymentId eq ${this.model.ServicePaymentId}`
      this.gridProgress.odata = this.odataProgress
      if (!this.gridProgress.data) {
        this.gridProgress.getData()
      }
      this.gridProgress.api.sizeColumnsToFit()
      this.scrollElementIntoView('#remit-progress-grid')
    }
  }

  toggleEncounterConfigComparison(): void {
    this.isExportViewShown = !this.isExportViewShown
    if (this.isExportViewShown) {
      this.gridExport.defaultFilter = `EdiFileRevisionId eq ${this.model.FileId}`
      this.gridExport.odata = this.odataExport
      if (!this.gridExport.data) {
        this.gridExport.getData()
      }
      this.gridExport.api.sizeColumnsToFit()
      this.scrollElementIntoView('#remit-export-grid')
    }
  }

  toggleSummaryConfigComparison(): void {
    if (this.isX12ViewShown) {
      this.closeX12View()
      return
    }
    if (
      this.summaryConfigComparisonSubscription &&
      !this.summaryConfigComparisonSubscription.closed
    ) {
      this.summaryConfigComparisonSubscription.complete()
    }
    this.grid.api.redrawRows()
    this.differences = 0
    this.currentDiff = 0
    const action = `?$filter=LeftEdiFileRevisionId eq ${this.model.FileId}`
    this.summaryConfigComparisonSubscription = this.odataX12
      .restGet(action)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(response => {
        const x12 = _.get(response[0], 'value[0]') as IRemitLineX12
        if (!x12) {
          this.toastr.error('Summary config comparison was not found.')
          return
        }
        this.isX12ViewShown = true

        const left = x12.Left.replace(/~/g, '~\r\n')
        const right = x12.Right.replace(/~/g, '~\r\n')

        this.differences = this.diff.compare(left, right)
        this.scrollElementIntoView('#remit-x12-comparison')
      }, this.showError.bind(this))
  }

  showAuditDetail(): void {
    this.isAuditView = !this.isAuditView
    this.scrollElementIntoView('#remit-auditDetail-grid')
  }

  refresh(): void {
    this.model = null
    this.hasSelection = false
    this.closePanels()
    this.grid.getData()
  }

  closePanels(): void {
    this.isProgressViewShown = false
    this.gridProgress.data = null
    this.gridProgress.clearFilter()
    this.gridProgress.clearSort()

    this.isExportViewShown = false
    this.gridExport.data = null
    this.gridExport.clearFilter()
    this.gridExport.clearSort()

    this.closeX12View()
    this.closeAuditView()
  }

  gotoDiff(offset: number): void {
    let id = `#${this.diff.resultid}-${this.currentDiff}`
    let elem: any = document.querySelector(id)
    if (elem) {
      elem.style.fontWeight = 'normal'
    }
    this.currentDiff += offset
    if (this.currentDiff < 0) {
      this.currentDiff = 1
    }
    if (this.currentDiff > this.differences) {
      this.currentDiff = this.differences
    }
    id = `#${this.diff.resultid}-${this.currentDiff}`
    elem = document.querySelector(id)
    if (elem) {
      elem.style.fontWeight = 'bold'
      elem.scrollIntoView({ block: 'nearest', inline: 'center' })
    }
  }

  canUnmatchClaim() {
    // this is dirty and should be done in the backend. k thanks. -JM
    const unmatchWhitelist = [
      'Payer Payment Sent',
      'Payer Denial Sent',
      'Contessa Denial Sent',
      'Contessa Force Payment Sent',
      'Contessa Payment Sent',
      'Payment Sent: Unknown Adjudication Type'
    ]

    return this.model.ClaimId && !unmatchWhitelist.includes(this.model.Status)
  }

  showMatchClaimModal() {
    return this.modalService
      .show(RemitListMatchClaimModalComponent, {
        class: 'modal-large'
      })
      .content.onClose.pipe(
        map(selectedClaim => {
          if (selectedClaim) {
            this.matchSelectedClaim(selectedClaim as IClaimLineView)
          }
        })
      )
      .subscribe()
  }

  matchSelectedClaim(selectedClaim: IClaimLineView) {
    // because the backend didn't put these in a request body as they should be
    const url = `${environment.rmapiroot}/ServiceLineResolver/ResolveServiceLine/${this.model.RemittanceId}/${selectedClaim.ClaimId}/${selectedClaim.ServiceId}/${this.model.ServicePaymentId}`
    const reqOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8',
        Accept: 'application/json'
      }),
      body: ''
    }

    const makeRequest = this.http.put(url, reqOptions).pipe(
      map(() => {
        this.toastr.success('Claim has been matched successfully')
        this.refresh()
      }),
      catchError(() => observableOf(this.toastr.error('There was an issue matching the claim')))
    )

    this.ensureMatchSelectedClaim()
      .pipe(
        mergeMap(isValidAction => {
          return isValidAction ? makeRequest : observableOf(null)
        })
      )
      .subscribe()
  }

  unmatchSelectedClaim() {
    // because the backend didn't put these in a request body as they should be

    const url = `${environment.rmapiroot}/ServiceLineResolver/UnresolveServiceLine/${this.model.ServicePaymentId} `
    const reqOptions = {
      headers: new Headers({
        'Content-Type': 'application/json; charset=utf-8',
        Accept: 'application/json'
      }),
      body: ''
    }

    const makeRequest = this.http.put(url, reqOptions).pipe(
      map(() => {
        this.toastr.success('Claim has been unmatched successfully')
        this.refresh()
      }),
      catchError(() => observableOf(this.toastr.error('There was an issue unmatching the claim')))
    )

    this.ensureUnmatchSelectedClaim()
      .pipe(
        mergeMap(isValidAction => {
          return isValidAction ? makeRequest : observableOf(null)
        })
      )
      .subscribe()
  }

  ensureMatchSelectedClaim() {
    return this.modalService
      .show(ConfirmModalComponent, {
        initialState: {
          title: `Match Claim`,
          message: `Are you sure you want to match with the selected claim?`
        }
      })
      .content.onClose.pipe(map(userConfirmation => userConfirmation))
  }

  ensureUnmatchSelectedClaim() {
    return this.modalService
      .show(ConfirmModalComponent, {
        initialState: {
          title: `Unmatch Claim`,
          message: `Are you sure you want to unmatch with the selected claim?`
        }
      })
      .content.onClose.pipe(map(userConfirmation => userConfirmation))
  }

  performAction(remitAction: IRemitLineAction): void {
    const url = `${environment.rmapiroot}/RemittanceActions/${remitAction.Uri}`
    const method = `${remitAction.HttpMethod.toLowerCase()}`
    const reqOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8',
        Accept: 'application/json'
      }),
      body: ''
    }

    const makeRequest = this.http.request(method, url, reqOptions)

    this.validateAction(remitAction)
      .pipe(
        mergeMap(isValidAction => {
          return isValidAction ? makeRequest : observableOf(null)
        })
      )
      .subscribe(() => {
        this.toastr.success(`${remitAction.ActionName} received.`)
      })
  }

  validateAction(remitAction: IRemitLineAction): Observable<boolean> {
    if (remitAction && remitAction.ActionName) {
      return this.showActionConfirmModal(remitAction)
    }
  }

  showActionConfirmModal(remitAction: IRemitLineAction): Observable<boolean> {
    return this.modalService
      .show(ConfirmModalComponent, {
        initialState: {
          title: `${remitAction.ActionName} Remit`,
          message: `Are you sure you want to ${remitAction.ActionName.toLowerCase()} this remit?`
        }
      })
      .content.onClose.pipe(map(userConfirmation => userConfirmation))
  }

  closeX12View(): void {
    this.isX12ViewShown = false
  }

  closeAuditView(): void {
    this.isAuditView = false
  }

  showError(response: HttpErrorResponse): void {
    const msg: string = this.util.getErrorString(response)

    this.toastr.error(msg)
  }

  configFieldChanged(e): void {
    this.odataExport
      .put(e.data, e.data.Id)
      .exec()
      .subscribe(
        () => {
          this.toastr.success('Updated successfully')
          this.auditDetail.Refresh()
          this.gridExport.getData()
        },
        () => {
          this.toastr.error('Error when updating config item')
          this.auditDetail.Refresh()
          this.gridExport.getData()
        }
      )
  }

  updateAuditDetail(data: IRemitLineExport) {
    const field = (data.ParentId ? data.ParentId + '/' : '') + data.SegmentId + '/' + data.ElementId

    this.auditDetail.auditInfo.push({
      FieldName: field,
      OldValue: data.OldValue,
      NewValue: data.NewValue,
      Date: moment().format(this.auditDetail.dateFormat),
      Username: this.user.FirstName + ' ' + this.user.LastName
    })

    this.auditDetail.gridAuditInfo.data = _.cloneDeep(this.auditDetail.auditInfo)
  }



  private scrollElementIntoView(selector): void {
    const el = document.querySelector(selector)
    const go = () => {
      el.scrollIntoView({ behavior: 'smooth' })
    }

    setTimeout(go, 100)
  }

  private initializeGrid(): void {
    this.gridActions = []
    this.grid.gridOptions.context = {
      canEdit: false,
      canDelete: false
    }
    this.grid.gridOptions.rowSelection = { mode: 'singleRow' }
    this.grid.gridOptions.columnDefs = [
      {
        headerName: 'Processing Status',
        field: 'Status',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 130,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Adjudication Type',
        field: 'AdjudicationTypeName',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Remittance ID',
        field: 'RemittanceId',
        width: 80,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'CHI Clm ID',
        field: 'ClaimId',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },

        width: 70,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Provider Clm ID',
        field: 'Identifier',
        width: 70,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Provider',
        field: 'ProviderName',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Payer Claim Control ID',
        field: 'PayerClaimControlId',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Patient',
        field: 'PatientName',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 120,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Auth Number',
        field: 'AuthorizationNumber',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'DOS',
        field: 'DateOfService',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 100,
        cellRenderer: ({ data: { DateOfService } }) => {
          const dos = DateOfService

          return moment(dos)
            .utc()
            .format('MM/DD/YYYY')
        }
      },
      {
        headerName: 'PX Code',
        field: 'ProcedureCode',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 80,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Claim Type',
        field: 'ClaimType',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Days Since Received',
        field: 'DaysSinceReceived',
        width: 100
      },
      {
        headerName: 'Patient Episode',
        field: 'PatientEpisodeId',
        width: 100,
        cellRenderer: EpisodeLinkRendererComponent
      },
      {
        headerName: 'Payer Response',
        field: 'ClaimStatusCode',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Billed Amount',
        field: 'BilledAmount',
        width: 100
      },
      {
        headerName: 'Allowed Amount',
        field: 'AllowedAmount',
        width: 100
      },
      {
        headerName: 'Paid Amount',
        field: 'PaidAmount',
        width: 100
      },
      {
        headerName: 'Patient Responsibility',
        field: 'PatientResponsibilityAmount',
        width: 100
      },
      {
        headerName: 'Days In Status',
        field: 'DaysInRemittanceStatus',
        width: 130
      }
    ]

    this.detailViewColumns = [
      {
        headerName: 'Remittance ID',
        field: 'RemittanceId'
      },
      {
        headerName: 'Service Payment ID',
        field: 'ServicePaymentId'
      },
      {
        headerName: 'Line',
        field: 'ServiceLineId'
      },
      {
        headerName: 'CHI Clm ID',
        field: 'ClaimId'
      },
      {
        headerName: 'File ID',
        field: 'FileId'
      },
      {
        headerName: 'Physician ID Code',
        field: 'PhysicianIdentificationCode'
      },
      {
        headerName: 'Provider',
        field: 'ProviderName'
      },
      {
        headerName: 'Payer Clm ID',
        field: 'PayerClaimControlId'
      },
      {
        headerName: 'Patient Id',
        field: 'PatientId'
      },
      {
        headerName: 'Patient',
        field: 'PatientName'
      },
      {
        headerName: 'Auth Number',
        field: 'AuthorizationNumber'
      },
      {
        headerName: 'DOS',
        field: 'DateOfService',
        cellRenderer: model => {
          const dos = model.DateOfService || model.StartDate

          return moment(dos)
            .utc()
            .format('MM/DD/YYYY')
        }
      },
      {
        headerName: 'Px / Rev Code',
        field: 'ProcedureCode'
      },
      {
        headerName: 'Modifier',
        field: 'ProcedureCodeModifier'
      },
      {
        headerName: 'Billed Amount',
        field: 'BilledAmount'
      },
      {
        headerName: 'Allowed Amount',
        field: 'AllowedAmount'
      },
      {
        headerName: 'Paid Amount',
        field: 'PaidAmount'
      },
      {
        headerName: 'Quantity',
        field: 'Quantity'
      },
      {
        headerName: 'Patient Responsibility',
        field: 'PatientResponsibilityAmount'
      },
      {
        headerName: 'Contractual Adj',
        field: 'ContractualAdjustmentAmount'
      },
      {
        headerName: 'Payer Response',
        field: 'ClaimStatusCode'
      },
      {
        headerName: 'Processing Status',
        field: 'Status'
      },
      {
        headerName: 'Days In Remit Status',
        field: 'DaysInRemittanceStatus'
      },
      {
        headerName: 'Provider Clm ID',
        field: 'Identifier'
      },
      {
        headerName: 'Plan',
        field: 'PlanName'
      },
      {
        headerName: 'Patient Episode',
        field: 'PatientEpisodeId'
      },
      {
        headerName: 'Claim Type',
        field: 'ClaimType'
      },
      {
        headerName: 'Days Since Received',
        field: 'DaysSinceReceived'
      },
      {
        headerName: 'Processing Type',
        field: 'ProcessingType'
      },
      {
        headerName: 'Validation Status',
        field: 'ValidationStatus'
      },
      {
        headerName: 'Days In Status',
        field: 'DaysInRemittanceStatus'
      },
      {
        headerName: 'Adjudication Type',
        field: 'AdjudicationTypeName'
      }
    ]

    this.grid.gridOptions.getRowStyle = (par: any) => {
      // if (!par || !par.data || !this.model) {
      //   return null;
      // }
      // if (par.data['ClaimId'] === this.model.ClaimId && par.data['ServiceLineId'] === this.model.ServiceLineId) {
      //   return {'color': 'black', 'background-color': 'LightBlue'}
      // }
      // else if (par.data['Identifier'] === this.model.Identifier) {
      //   return {'color': 'black', 'background-color': 'Plum'}
      // }
      return null
    }

    this.grid.gridOptions.onSelectionChanged = this.gridSelectionChanged.bind(this)
    this.grid.defaultOrder = [{ colId: 'ClaimId', sort: 'desc' }]
    this.grid.defaultSelect = [
      'Id', 'RemittanceId', 'ServicePaymentId', 'ServiceLineId', 'ClaimId', 'FileId',
      'Status', 'AdjudicationTypeName', 'Identifier', 'ProviderName', 'PayerClaimControlId',
      'PhysicianIdentificationCode', 'PatientId', 'PatientName', 'AuthorizationNumber',
      'DateOfService', 'ProcedureCode', 'ProcedureCodeModifier', 'ClaimType',
      'DaysSinceReceived', 'PatientEpisodeId', 'ClaimStatusCode',
      'BilledAmount', 'AllowedAmount', 'PaidAmount', 'Quantity',
      'PatientResponsibilityAmount', 'ContractualAdjustmentAmount',
      'DaysInRemittanceStatus', 'PlanName', 'ProcessingType', 'ValidationStatus',
      'IsDataEditable'
    ].join(',')
    this.grid.odata = this.odata
  }

  private initializeProgressGrid(): void {
    this.gridProgress.gridOptions.columnDefs = [
      {
        headerName: 'Validation',
        field: 'Validation',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 200,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Status',
        field: 'Status',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Field',
        field: 'Field',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 150,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Field Value',
        field: 'FieldValue',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 120,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Timestamp',
        field: 'TimeStamp',
        filter: 'agDateColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 150,
        cellRenderer: GridWrapperComponent.DateRenderer('L LT')
      },
      {
        headerName: 'Message',
        field: 'ShortMessage',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 200,
        cellRenderer: params => {
          let value = [].concat(params.value || []).join(', ')
          value = this.sanitizer.sanitize(SecurityContext.HTML, value)
          return `<span title="${value}">${value}</span>`
        }
      }
    ]
    this.gridProgress.defaultOrder = [{ colId: 'TimeStamp', sort: 'asc' }]
  }

  private initializeExportGrid(): void {
    // Encounter Config Comparison
    this.gridExport.gridOptions.columnDefs = [
      {
        headerName: 'Configuration Field',
        field: 'FieldName',
        // TODO: Enable filters once Config Field runs server-side
        // filter: 'agTextColumnFilter',
        // filterParams: {  newRowsAction: 'keep' },
        menuTabs: [],
        sortable: false,
        width: 400,
        cellRenderer: params => {
          const data = params.data as IRemitLineExport
          const match = X12Codes.Find('835', data.SegmentId, data.ElementId, data.NewValue)
          if (match) {
            return `<span title="${match.ToolTip || ''}">${match.FriendlyName}</span>`
          }

          return ''
        }
      },
      {
        headerName: '835 In',
        field: 'OldValue',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 150,
        cellRenderer: (params: any) => {
          const def = X12Codes.Find(
            '835',
            params.data.SegmentId,
            params.data.ElementId,
            params.data.OldValue
          )
          const formatted = X12Codes.FormatValue(params.data.OldValue, def)
          return `<span title="${formatted}">${formatted}</span>`
        }
      },
      {
        headerName: '835 Out',
        field: 'NewValue',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 150,
        editable: () => this.model.IsDataEditable,
        cellRenderer: (params: any) => {
          const def = X12Codes.Find(
            '835',
            params.data.SegmentId,
            params.data.ElementId,
            params.data.NewValue
          )
          const formatted = X12Codes.FormatValue(params.data.NewValue, def)
          return `<span title="${formatted}">${formatted}</span>`
        },
        cellStyle: () => (this.model.IsDataEditable ? { 'background-color': 'lightBlue' } : {})
      },
      {
        headerName: 'Parent Segment',
        field: 'ParentId',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 70,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'Segment',
        field: 'SegmentId',
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 70,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'ElementId',
        field: 'ElementId',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        width: 70,
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      }
    ]
    this.gridExport.pageSize = 100
  }

  showDeleteRemittanceModal() {
    this.modalService.show(RemitDeleteModalComponent, {
      initialState: {
        model: this.model,
        onSuccess: () => this.grid.getData()
      }
    })
  }
}

@Component({
  templateUrl: './remit-delete-modal.component.html',
  standalone: false
})
export class RemitDeleteModalComponent {
  model: IRemitLineView
  onSuccess: () => void
  remitDeleteReason = ''
  constructor(
    public modalRef: BsModalRef,
    private remittanceservice: RemittanceService,
    private toastr: ToastrService
  ) { }
  deleteRemittance(event: any) {
    const deleteDto: RemittanceDeleteDto = {
      RemittanceId: this.model.RemittanceId,
      Reason: this.remitDeleteReason
    }

    this.remittanceservice.deleteRemittance(deleteDto).subscribe(
      () => {
        this.toastr.success('Claim Deleted')
        event.target.disabled = false
        this.modalRef.hide()
        this.onSuccess()
      },
      error => {
        this.toastr.error('Error: ' + error.statusText)
        event.target.disabled = false
      }
    )
  }
}
